import { useEffect, useState } from 'react';
import api from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Building2 } from 'lucide-react';

const NUM_FIELDS = [
  { key: 'usd_per_month', label: 'Price ($/mo)', step: '1' },
  { key: 'usd_per_year', label: 'Price ($/yr)', step: '1' },
  { key: 'bill_credits_per_month', label: 'Bill credits / mo', step: '1' },
  { key: 'ai_credits_grant_at_signup', label: 'AI credits (welcome)', step: '1' },
  { key: 'ai_credits_grant_per_month', label: 'AI credits / mo', step: '1' },
  { key: 'overage_per_bill_usd', label: 'Overage $/bill', step: '0.01' },
  { key: 'trial_days', label: 'Trial days', step: '1' },
];

// 1 AI credit = $0.10 (fixed valuation, see BILLING_MODEL).
const AI_CREDIT_USD = 0.10;

const EMPTY = {
  label: '', tagline: '', usd_per_month: '', usd_per_year: '', bill_credits_per_month: '',
  ai_credits_grant_at_signup: 0, ai_credits_grant_per_month: 0, overage_per_bill_usd: '',
  trial_days: 0, stripe_bypass: true, visible: false, active: true, cta_url: '',
};

export default function CustomPlansPanel() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // plan obj or 'new'
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/super-admin/subscription/custom-plans');
      setPlans(data?.custom_plans || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load custom plans'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setDraft({ ...EMPTY }); setEditing('new'); };
  const openEdit = (p) => {
    const d = { ...EMPTY, ...p };
    NUM_FIELDS.forEach(({ key }) => { d[key] = p[key] ?? ''; });
    setDraft(d); setEditing(p);
  };

  const setF = (k, v) => setDraft((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!draft.label?.trim()) { toast.error('Plan name is required'); return; }
    setSaving(true);
    try {
      const patch = { label: draft.label, tagline: draft.tagline,
        stripe_bypass: draft.stripe_bypass, visible: draft.visible, active: draft.active,
        cta_url: draft.cta_url || null };
      NUM_FIELDS.forEach(({ key }) => {
        const v = draft[key];
        patch[key] = (v === '' || v === null || v === undefined) ? null : Number(v);
      });
      if (editing === 'new') {
        await api.post('/super-admin/subscription/custom-plans', patch);
        toast.success('Custom plan created');
      } else {
        await api.put(`/super-admin/subscription/custom-plans/${editing.id}`, patch);
        toast.success('Custom plan updated');
      }
      setEditing(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/super-admin/subscription/custom-plans/${toDelete.id}`);
      toast.success('Custom plan deleted');
      setToDelete(null); await load();
    } catch (e) {
      // 409 = still assigned to orgs (guarded)
      toast.error(e.response?.data?.detail || 'Delete failed');
      setToDelete(null);
    }
  };

  if (loading) return <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div data-testid="custom-plans-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-600 max-w-2xl">
          Bespoke / managed-service plans. Hidden from the public picker; may bypass Stripe. Assign them to specific orgs on the Org Allocation tab.
        </p>
        <Button onClick={openNew} className="rounded-none shrink-0" size="sm" data-testid="custom-plan-new-btn">
          <Plus className="h-4 w-4 mr-1" /> New custom plan
        </Button>
      </div>

      {plans.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-10 text-center text-sm text-slate-400" data-testid="custom-plans-empty">
          No custom plans yet. Create one for a managed-service or negotiated Enterprise customer.
        </div>
      ) : (
        <div className="space-y-3">
          {plans.map((p) => (
            <div key={p.id} className="bg-white border border-slate-200 px-5 py-3 flex items-center justify-between" data-testid={`custom-plan-${p.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>{p.label}</span>
                  <Badge variant="outline" className="rounded-none text-[10px] text-violet-600 border-violet-300">custom</Badge>
                  {p.stripe_bypass && <Badge variant="outline" className="rounded-none text-[10px] text-amber-600 border-amber-300">bypasses Stripe</Badge>}
                  {!p.active && <Badge variant="outline" className="rounded-none text-[10px] text-rose-600 border-rose-300">inactive</Badge>}
                  {p.visible && <Badge variant="outline" className="rounded-none text-[10px] text-emerald-600 border-emerald-300">visible</Badge>}
                </div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                  <span>${p.usd_per_month ?? '—'}/mo · ${p.usd_per_year ?? '—'}/yr</span>
                  <span>{p.bill_credits_per_month ?? '∞'} bill credits</span>
                  <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{(p.assigned_org_ids || []).length} org(s)</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="rounded-none" onClick={() => openEdit(p)} data-testid={`custom-plan-edit-${p.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" className="rounded-none text-rose-600" onClick={() => setToDelete(p)} data-testid={`custom-plan-delete-${p.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="custom-plan-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>{editing === 'new' ? 'New custom plan' : 'Edit custom plan'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Plan name</label>
                <Input value={draft.label} onChange={(e) => setF('label', e.target.value)} className="rounded-none mt-1" data-testid="custom-plan-label" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Tagline</label>
                <Input value={draft.tagline} onChange={(e) => setF('tagline', e.target.value)} className="rounded-none mt-1" data-testid="custom-plan-tagline" />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {NUM_FIELDS.map(({ key, label, step }) => (
                <div key={key}>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</label>
                  <Input type="number" step={step} value={draft[key] ?? ''} onChange={(e) => setF(key, e.target.value)} className="rounded-none mt-1 font-mono text-sm" data-testid={`custom-plan-${key}`} />
                  {key.startsWith('ai_credits_grant') && (
                    <p className="text-[10px] text-emerald-600 mt-1 font-mono" data-testid={`custom-plan-${key}-usd`}>
                      = ${((Number(draft[key]) || 0) * AI_CREDIT_USD).toFixed(2)}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">External CTA URL (optional — opens instead of Stripe checkout)</label>
              <Input value={draft.cta_url ?? ''} onChange={(e) => setF('cta_url', e.target.value)} placeholder="https://billportal.io/contact" className="rounded-none mt-1 font-mono text-sm" data-testid="custom-plan-cta_url" />
            </div>
            <div className="flex flex-wrap items-center gap-6 pt-2 border-t border-slate-100">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Switch checked={draft.stripe_bypass} onCheckedChange={(v) => setF('stripe_bypass', v)} data-testid="custom-plan-bypass" />
                Bypass Stripe (managed billing)
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Switch checked={draft.active} onCheckedChange={(v) => setF('active', v)} data-testid="custom-plan-active" />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Switch checked={draft.visible} onCheckedChange={(v) => setF('visible', v)} data-testid="custom-plan-visible" />
                Visible to end users
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="rounded-none" onClick={save} disabled={saving} data-testid="custom-plan-save">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm (with allocation warning) */}
      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent data-testid="custom-plan-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{toDelete?.label}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {(toDelete?.assigned_org_ids || []).length > 0 ? (
                <span className="text-rose-600 font-medium">
                  This plan is still assigned to {(toDelete?.assigned_org_ids || []).length} organization(s).
                  You must unassign every org (Org Allocation tab) before it can be deleted — otherwise the
                  deletion will be blocked to avoid breaking those customers.
                </span>
              ) : 'This custom plan is not assigned to any org and can be safely deleted.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-none bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="custom-plan-delete-yes">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
